import dayjs from 'dayjs';
import { RefObject, useRef } from 'react';
import { computeDateRange, isSameDateRange } from '../../../helpers/date';
import DatesRangeSelector from '../../controls/DatesRangeSelector';

interface StringDateRange {
  start: string;
  end: string;
  flexibleDates: number;
  availableDays: number;
  checkedFlexibleDates: boolean;
  enabledFlexibleDates: boolean;
}

interface NumberDateRange {
  start: number;
  end: number;
}

interface DateRange {
  start: Date;
  end: Date;
}

/**
 * determine dateRange based on prepopulation's values (departure, dateRange)
 * @param {*} param
 * @param {Number} maxItemsAmount
 * @param nextMonth
 */
export const getDateRange = (
  { departure, dateRange, ...rest }: { departure: NumberDateRange; dateRange: NumberDateRange },
  maxItemsAmount?: number,
  nextMonth?: boolean,
  defaultMonth?: number
): Partial<DateRange> => {
  if (!maxItemsAmount) {
    return computeDateRange(dateRange);
  }
  let startRange = 0;
  const range = dateRange || departure;
  if (range && range.start) {
    startRange = dateRange.start;
  }
  let start = dayjs().add(startRange, 'day');
  if (defaultMonth && defaultMonth > 1) {
    start = start.add(defaultMonth - 1, 'month').startOf('month');
  }
  if (nextMonth && (defaultMonth === undefined || defaultMonth === 1)) {
    start = start.add(maxItemsAmount, 'month').startOf('month');
  }
  return {
    start: start.toDate(),
    end: start
      .add(maxItemsAmount - 1, 'month')
      .endOf('month')
      .toDate()
  };
};

type DateRangeSelectorRef = RefObject<Pick<DatesRangeSelector, 'clear'>>;

interface UseDateRange {
  value: StringDateRange;
  defaultValue: StringDateRange;
  onChange: (value: StringDateRange) => void;
  onClear: () => void;
  clear: (requestFocus?: boolean) => void;
  ref: DateRangeSelectorRef;
}

export function useDateRange({ dispatch, value }: $TSFixMe): UseDateRange {
  const ref: DateRangeSelectorRef = useRef(null);
  const onChange: UseDateRange['onChange'] = (dateRange) => {
    if (
      !isSameDateRange(dateRange, value) ||
      dateRange.flexibleDates !== value.flexibleDates ||
      dateRange.checkedFlexibleDates !== value.checkedFlexibleDates ||
      dateRange.enabledFlexibleDates !== value.enabledFlexibleDates
    ) {
      dispatch({ type: 'DATE_RANGE_CHANGE', payload: dateRange });
    }
  };

  const onClear = (): void => {
    dispatch({ type: 'DATE_RANGE_CLEAR' });
  };

  const clear: UseDateRange['clear'] = (requestFocus = false): void => {
    if (ref.current) {
      ref.current.clear(requestFocus);
    }
  };

  return { value, defaultValue: value, onChange, onClear, ref, clear };
}
